// stepper-navigation.service.ts
import {
  Injectable,
  signal,
  computed,
  inject,
  OnDestroy,
} from '@angular/core';

import {
  NavigationEnd,
  NavigationStart,
  Router,
  Event as RouterEvent,
} from '@angular/router';

import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StepperNavigationService implements OnDestroy {

  private router = inject(Router);

  private backHandler = signal<(() => boolean) | null>(null);
  private handlerOwner = signal<string | null>(null);

  /**
   * Navigation state
   */
  private lastNavigationWasBrowserBack = signal(false);
  private lastNavigationWasAppBack = signal(false);

  /**
   * Internal cleanup
   */
  private resetTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private subscriptions = new Subscription();

  hasActiveHandler = computed(() => this.backHandler() !== null);

  constructor() {

    /**
     * Detect browser back/forward
     */
    this.subscriptions.add(
      this.router.events
        .pipe(
          filter(
            (event: RouterEvent): event is NavigationStart =>
              event instanceof NavigationStart,
          ),
        )
        .subscribe((event: NavigationStart) => {

          /**
           * popstate = browser back/forward
           */
          const isBrowserBack =
            event.navigationTrigger === 'popstate';

          this.lastNavigationWasBrowserBack.set(
            isBrowserBack,
          );

          /**
           * Browser navigation should clear app flag
           */
          if (isBrowserBack) {
            this.lastNavigationWasAppBack.set(false);
          }
        }),
    );

    /**
     * Reset flags after navigation completes
     */
    this.subscriptions.add(
      this.router.events
        .pipe(
          filter(
            (event: RouterEvent): event is NavigationEnd =>
              event instanceof NavigationEnd,
          ),
        )
        .subscribe(() => {

          this.clearFlagsAfterNavigation();

        }),
    );
  }

  /**
   * App Back Button
   */
  markAppBackNavigation(): void {

    this.clearTimeout();

    this.lastNavigationWasAppBack.set(true);
    this.lastNavigationWasBrowserBack.set(false);
  }

  /**
   * Reset flags shortly after navigation
   */
  private clearFlagsAfterNavigation(): void {

    this.clearTimeout();

    this.resetTimeoutId = setTimeout(() => {

      this.lastNavigationWasAppBack.set(false);
      this.lastNavigationWasBrowserBack.set(false);

      this.resetTimeoutId = null;

    }, 100);
  }

  /**
   * Timeout cleanup helper
   */
  private clearTimeout(): void {

    if (this.resetTimeoutId) {

      clearTimeout(this.resetTimeoutId);

      this.resetTimeoutId = null;
    }
  }

  /**
   * Queries
   */
  wasBrowserBackNavigation(): boolean {
    return this.lastNavigationWasBrowserBack();
  }

  wasAppBackNavigation(): boolean {
    return this.lastNavigationWasAppBack();
  }

  wasAnyBackNavigation(): boolean {
    return (
      this.lastNavigationWasBrowserBack() ||
      this.lastNavigationWasAppBack()
    );
  }

  shouldCleanup(): boolean {
    return !this.wasAnyBackNavigation();
  }

  /**
   * Register handler
   */
  registerBackHandler(
    fn: () => boolean,
    owner?: string,
  ): void {

    this.backHandler.set(fn);
    this.handlerOwner.set(owner ?? null);
  }

  /**
   * Clear handler
   */
  clearBackHandler(owner?: string): void {

    if (
      owner &&
      this.handlerOwner() &&
      owner !== this.handlerOwner()
    ) {
      return;
    }

    this.backHandler.set(null);
    this.handlerOwner.set(null);
  }

  /**
   * Execute handler
   */
  handleBack(): boolean {

    this.markAppBackNavigation();

    const handler = this.backHandler();

    if (!handler) {
      return false;
    }

    try {

      return handler();

    } catch (error) {

      console.error(
        'Back handler error:',
        error,
      );

      return false;
    }
  }

  /**
   * Active owner
   */
  getActiveOwner(): string | null {
    return this.handlerOwner();
  }

  /**
   * FULL RESET
   */
  reset(): void {

    /**
     * Clear handlers
     */
    this.backHandler.set(null);
    this.handlerOwner.set(null);

    /**
     * Clear navigation flags
     */
    this.lastNavigationWasBrowserBack.set(false);
    this.lastNavigationWasAppBack.set(false);

    /**
     * Clear pending timers
     */
    this.clearTimeout();
  }

  /**
   * Destroy service
   */
  ngOnDestroy(): void {

    this.reset();

    this.subscriptions.unsubscribe();
  }
}
